// src/scenes/auth/ResetPassword.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, TextField, Typography, Alert, IconButton, InputAdornment, CircularProgress } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import figLogo from '../../assets/fig_CROP.png';
import axios from 'axios';
import bcrypt from 'bcryptjs';

const API_BASE = 'https://figazuresql.swancreate.com';
const API_USERS = `${API_BASE}/api/swa/UserMst`;
const TTL_MINUTES = 60; // optional: enforce a time window using UpdatedOn if present

export default function ResetPassword() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const token = params.get('t') || params.get('token') || '';
    const apiid = params.get('u') || params.get('user') || '';

    const [busy, setBusy] = useState(true);
    const [user, setUser] = useState(null);
    const [err, setErr] = useState('');
    const [p1, setP1] = useState('');
    const [p2, setP2] = useState('');
    const [show, setShow] = useState(false);
    const [done, setDone] = useState(false);

    const mismatch = useMemo(() => p1 && p2 && p1 !== p2, [p1, p2]);

    useEffect(() => {
        (async () => {
            setBusy(true);
            setErr('');
            try {
                // try GET by apiid; fallback to list + find
                let u = null;
                try {
                    const { data } = await axios.get(`${API_USERS}/${apiid}`);
                    u = data;
                } catch {
                    const { data } = await axios.get(API_USERS);
                    u = (data || []).find(x => x.apiid === apiid);
                }
                if (!u) return setErr('Invalid or expired reset link.'), setBusy(false);

                // verify token matches PasswordResetId
                if (!u.PasswordResetId || String(u.PasswordResetId).toLowerCase() !== String(token).toLowerCase()) {
                    setErr('Invalid or expired reset link.');
                    setBusy(false);
                    return;
                }

                // optional TTL using UpdatedOn if present
                if (u.UpdatedOn) {
                    const updated = new Date(u.UpdatedOn);
                    const minutes = (Date.now() - updated.getTime()) / 60000;
                    if (minutes > TTL_MINUTES) {
                        setErr('Reset link has expired.');
                        setBusy(false);
                        return;
                    }
                }

                setUser(u);
            } catch (e) {
                console.error(e);
                setErr('Unable to verify reset link.');
            } finally {
                setBusy(false);
            }
        })();
    }, [apiid, token]);

    const handleSubmit = async () => {
        setErr('');
        if (!user) return setErr('Invalid reset request.');
        if (!p1 || !p2) return setErr('Please enter and confirm your new password.');
        if (mismatch) return setErr('Passwords do not match.');

        try {
            setBusy(true);
            const hashed = bcrypt.hashSync(p1, 10); // same as Team page
            await axios.put(`${API_USERS}/${apiid}`, {
                Password: hashed,
                PasswordResetId: null,
            });
            setDone(true);
            setTimeout(() => navigate('/login'), 1800);
        } catch (e) {
            console.error(e);
            setErr('Unable to reset password.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
            <Box sx={{ width: 420, p: 4, borderRadius: 2, boxShadow: 3, bgcolor: 'background.paper', textAlign: 'center' }}>
                <img src={figLogo} alt="FIG" style={{ width: 96, marginBottom: 12 }} />
                <Typography variant="h5" gutterBottom>Choose a new password</Typography>

                {busy ? (
                    <Box sx={{ py: 3 }}><CircularProgress /></Box>
                ) : done ? (
                    <Alert severity="success" sx={{ mb: 2 }}>Password updated. Redirecting to login�</Alert>
                ) : (
                    <>
                        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

                        {!err && (
                            <>
                                <TextField
                                    fullWidth margin="normal" label="New password" type={show ? 'text' : 'password'}
                                    value={p1} onChange={e => setP1(e.target.value)}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => setShow(s => !s)} edge="end">
                                                    {show ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                                <TextField
                                    fullWidth margin="normal" label="Confirm password" type={show ? 'text' : 'password'}
                                    value={p2} onChange={e => setP2(e.target.value)} error={mismatch}
                                    helperText={mismatch ? 'Passwords do not match.' : ' '}
                                />
                                <Button variant="contained" fullWidth onClick={handleSubmit} disabled={busy} sx={{ mt: 1 }}>
                                    Save new password
                                </Button>
                                <Typography variant="body2" sx={{ mt: 2 }}>
                                    <Link to="/login">Back to login</Link>
                                </Typography>
                            </>
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
}
